import * as React from "react";
import { useWebSocket } from "../../../hooks/useWebSocket";
import {
  commandChannel,
  fetchCommandExecution,
  fetchCommandExecutions,
  fetchProductCommands,
  type CommandExecution,
  type ProductCommandsResponse,
} from "../../../lib/api/commands";

const RECENT_LIMIT = 10;
const IN_FLIGHT_POLL_MS = 3000;

/** Loads the product's command list; `null` data with status "denied" means the user has no command access. */
export function useProductCommands(productId: string, token: string | null) {
  const [data, setData] = React.useState<ProductCommandsResponse | null>(null);
  const [status, setStatus] = React.useState<"loading" | "ready" | "denied" | "error">("loading");

  const load = React.useCallback(async () => {
    if (!token) return;
    try {
      setData(await fetchProductCommands(token, productId));
      setStatus("ready");
    } catch (err) {
      const httpStatus = (err as { status?: number }).status;
      setData(null);
      setStatus(httpStatus === 403 || httpStatus === 404 ? "denied" : "error");
    }
  }, [productId, token]);

  React.useEffect(() => {
    setStatus("loading");
    void load();
  }, [load]);

  return { data, status, reload: load };
}

function upsert(list: CommandExecution[], row: CommandExecution): CommandExecution[] {
  const without = list.filter((r) => r.id !== row.id);
  return [row, ...without].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** Recent executions for one product, kept live from the `commands:products:{id}` channel. */
export function useProductCommandExecutions(productId: string, token: string | null, enabled: boolean) {
  const { subscribe, unsubscribe, isConnected } = useWebSocket();
  const [executions, setExecutions] = React.useState<CommandExecution[]>([]);
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(
    async (cursor?: string | null) => {
      if (!token || !enabled) return;
      setLoading(true);
      try {
        const page = await fetchCommandExecutions(token, { productId, cursor, limit: RECENT_LIMIT });
        setExecutions((prev) => (cursor ? [...prev, ...page.data] : page.data));
        setNextCursor(page.nextCursor);
      } finally {
        setLoading(false);
      }
    },
    [productId, token, enabled],
  );

  React.useEffect(() => {
    void load();
  }, [load]);

  const track = React.useCallback((row: CommandExecution) => {
    setExecutions((prev) => upsert(prev, row));
  }, []);

  React.useEffect(() => {
    if (!enabled) return;
    const topic = commandChannel(productId);
    const handler = (msg: { type: string; payload?: unknown }) => {
      if (msg.type !== "command.updated" || !msg.payload) return;
      track(msg.payload as CommandExecution);
    };
    subscribe([topic], handler);
    return () => unsubscribe([topic], handler);
  }, [productId, enabled, subscribe, unsubscribe, track]);

  const inFlight = executions.find((e) => e.status === "sent" && e.source !== "smart-web-observed") ?? null;

  // Fallback while the socket is down: poll the in-flight command until it settles.
  React.useEffect(() => {
    if (!inFlight || isConnected || !token) return;
    const id = setInterval(() => {
      void fetchCommandExecution(token, inFlight.id).then(track).catch(() => undefined);
    }, IN_FLIGHT_POLL_MS);
    return () => clearInterval(id);
  }, [inFlight, isConnected, token, track]);

  return {
    executions,
    inFlight,
    hasMore: Boolean(nextCursor),
    loading,
    loadMore: () => load(nextCursor),
    track,
  };
}
